/**
 * MapZoneFocus — highlight the focused zone on the map.
 *
 * Applies the selected-zone highlight once the map is loaded, re-running when
 * the zone changes (zone switching) so the outline tracks the current zone.
 * Cleans up the highlight on unmount. Moves the camera only on an explicit
 * focus intent (`focusRequested`, set by a zone-index row click) — `?zone=`
 * on its own never moves it, so the map stays wherever the BD left it.
 *
 * Rendered by {@link MapSurface} while a zone is selected and loaded. It is a
 * separate component so it can own hooks (useEffect, useRef) without violating
 * the rule against hooks inside render callbacks. When the zone closes it
 * unmounts and the cleanup removes the highlight; the persistent MapShell it
 * draws onto stays mounted.
 */
import { bbox } from '@turf/turf';
import type { Geometry } from 'geojson';
import { useEffect, useRef } from 'react';
import type { MapShellRenderState } from '@/features/locations/components/MapShell';
import { keptMapPitch } from '@/features/locations/utils/basemap';
import { MapLayersService } from '@/features/locations/utils/MapLayersService';
import type { IZone } from '@/types/api';
import { cameraDuration, FIT_PADDING, INITIAL_FIT_DURATION_MS } from '../utils/cameraMotion';

/** Zoom used when a zone has no extent to fit (a Point geometry). */
const POINT_ZONE_ZOOM = 13;

interface MapZoneFocusProps {
  shell: MapShellRenderState;
  zone: IZone;
  /**
   * The zone the camera has been asked to move to, or null. Identified by id
   * rather than a boolean so a second request for a different zone is a new
   * intent — a zone→zone hop or a close-and-reopen carries none and holds still.
   */
  focusZoneId: string | null;
  /** Spends the intent. Called on every exit path, fit or not. */
  onFocusConsumed: () => void;
}

export function MapZoneFocus({ shell, zone, focusZoneId, onFocusConsumed }: MapZoneFocusProps) {
  const { map, isMapLoaded } = shell;
  const highlightedZoneIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (focusZoneId !== zone.id) return;
    if (!isMapLoaded) return;
    const currentMap = map.current;
    if (!currentMap) return;

    try {
      const [minLng, minLat, maxLng, maxLat] = bbox(zone.geometry as unknown as Geometry);
      // Unfittable geometry still consumes the intent, otherwise the next
      // zone→zone hop would inherit it and fit a zone nobody asked for.
      if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) {
        onFocusConsumed();
        return;
      }

      if (minLng === maxLng && minLat === maxLat) {
        currentMap.easeTo({
          center: [minLng, minLat],
          zoom: POINT_ZONE_ZOOM,
          duration: cameraDuration(INITIAL_FIT_DURATION_MS),
        });
      } else {
        currentMap.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          {
            padding: FIT_PADDING,
            duration: cameraDuration(INITIAL_FIT_DURATION_MS),
            pitch: keptMapPitch(currentMap),
          },
        );
      }

      onFocusConsumed();
    } catch {
      // Silently ignore fit errors — the map remains usable where it is. Same
      // reasoning as the non-finite guard: the intent is spent either way.
      onFocusConsumed();
    }
  }, [zone, isMapLoaded, map, focusZoneId, onFocusConsumed]);

  // Not gated on the icons: the low-zoom mark names an image initializeMapIcons
  // registers, and Mapbox paints the symbol once that image lands.
  useEffect(() => {
    if (!isMapLoaded) return;
    const currentMap = map.current;
    if (!currentMap) return;

    try {
      // Skip when the focused zone has not changed — avoids redundant highlight
      // calls on unrelated re-renders (e.g. overlay toggle, viewport refetches).
      if (highlightedZoneIdRef.current === zone.id) return;

      // Idempotent; setData on re-invoke so switching zones moves the
      // highlight in place.
      new MapLayersService(currentMap).addSelectedZoneHighlight({
        geometry: zone.geometry,
        name: zone.name,
      });

      highlightedZoneIdRef.current = zone.id;
    } catch {
      // Silently ignore highlight errors — the map remains usable.
    }
  }, [zone, isMapLoaded, map]);

  // Tear down the highlight when leaving the view so a later view doesn't
  // inherit a stale outline.
  //
  // Resetting the ref here (not just removing the layers) matters in dev:
  // StrictMode mounts every effect, tears it down, then mounts it again to
  // surface missing cleanups. That synthetic teardown runs this cleanup
  // right after the effect above has already set `highlightedZoneIdRef` and
  // added the highlight — removing the layers but leaving the ref pointed at
  // `zone.id`. Without the reset, the effect above's `remount` sees the ref
  // already matching `zone.id` and skips re-adding, so the highlight never
  // reappears until the zone actually changes. Resetting to null makes the
  // synthetic remount re-add it, and is a no-op on a real unmount.
  // biome-ignore lint/correctness/useExhaustiveDependencies: map is a stable ref; cleanup-only effect
  useEffect(() => {
    return () => {
      if (map.current) {
        new MapLayersService(map.current).removeSelectedZoneHighlight();
      }
      highlightedZoneIdRef.current = null;
    };
  }, []);

  // Renders nothing — it only drives the highlight side effect.
  return null;
}
